import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useItemCurrencies } from '@/hooks/item';
import { useTalononeCampaigns, useTalononeNewPromoCode, useTalononePromoResponse } from '@/hooks/talonone';
import { TalononeNewPromoCodeModal } from '@/types/modal';
import { ItemCurrency } from '@/types/item';
import { TalonOneCampaign } from '@/types/talonone';
import TalonOneDatePicker from '@/components/molecules/common/TalononeDatePicker';
import { Dropdown, Input, Radio } from '@/components/molecules/common/Form';
import { CopyModal, ErrorModal, LoadingModal, StandardModal } from '@/components/molecules/common/Modal';
import { NewPromoCodeData } from '@/services/talonone';
import styles from './Modal.module.scss';

// TODO: Make small state
type State = {
  selectedCampaign: TalonOneCampaign | null;
  discount: 'None' | 'Amount' | 'Percentage';
  amountCurrency: ItemCurrency | null;
  amountAmount: number | null;
  amountMinOrder: number | null;
  percentageCurrency: ItemCurrency | null;
  percentageAmount: number | null;
};

const View: React.FC<{ modal: TalononeNewPromoCodeModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { client, onClose, pageSize } = modal;

  const currentTime = useRef(dayjs().format('DD-MM-YYYY HH:mm:ss'));
  const initState: State = {
    selectedCampaign: null,
    discount: 'None',
    amountCurrency: null,
    amountAmount: 10,
    amountMinOrder: 1,
    percentageCurrency: null,
    percentageAmount: 10,
  };
  const [state, updateState] = useState<State>(initState);
  const [date, setDate] = useState<string | null>(null);
  const { refetch } = useTalononePromoResponse(client.clientId, 0, pageSize, true);

  const currencyR = useItemCurrencies();
  const campaignsR = useTalononeCampaigns();
  const submitR = useTalononeNewPromoCode();

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      refetch();
    }
  }, [submitR.kind]);

  useEffect(() => {
    if (currencyR.kind === 'Loaded' && campaignsR.kind === 'Loaded') {
      updateState({
        ...state,
        selectedCampaign: filterCampaigns(campaignsR.data)[0],
        amountCurrency: { code: '', decimal_digits: 0 },
        percentageCurrency: { code: '', decimal_digits: 0 },
      });
    }
  }, [currencyR.kind, campaignsR.kind]);

  if (currencyR.kind === 'Called' || campaignsR.kind === 'Called') {
    return (
      <LoadingModal
        title={t('modal.talonone_create.title')}
        onClose={onClose}
      />
    );
  } else if (currencyR.kind === 'Error' || campaignsR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.talonone_create.title')}
        message={t('modal.talonone_create.get_data_error')}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.talonone_create.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    const { code, couponDiscountLimit, couponCurrency } = submitR.data;
    return (
      <CopyModal
        title={t('modal.talonone_create.title_success')}
        beforeInfo={
          <>
            {couponDiscountLimit && couponCurrency && (
              <div className={styles.paddingY1rem}>{`${t(
                'modal.talonone_create.success_with_limit',
              )} ${couponCurrency} ${couponDiscountLimit}.`}</div>
            )}
            <div className={styles.height1rem} />
            <div className={clsx([styles.text075rem, styles.textGrey])}>{t('modal.talonone_create.title')}</div>
            <div className={styles.height05rem} />
          </>
        }
        afterInfo={<br />}
        textToCopy={code}
        onClose={onClose}
        containerStyle={{ width: 500 }}
      />
    );
  }

  const currencies = currencyR.data;
  const campaigns = filterCampaigns(campaignsR.data);
  const {
    selectedCampaign,
    discount,
    amountCurrency,
    amountAmount,
    amountMinOrder,
    percentageCurrency,
    percentageAmount,
  } = state;

  const onChangeTemplate = (value: string) => {
    const item = campaigns.find((campaign) => campaign.id === Number(value));
    updateState({
      ...state,
      selectedCampaign: item || null,
      discount: item && item.campaignType !== 'replacement-voucher' ? 'None' : discount,
    });
  };

  const body = (
    <div className={styles.flex100}>
      <div className={styles.height05rem} />
      <div className={styles.text1rem}>
        {t('modal.talonone_create.for_email')} <strong>{client.email}</strong>
      </div>
      <div className={styles.height05rem} />
      <div className={styles.height1rem} />
      <Dropdown
        label={t('modal.talonone_create.template')}
        required={true}
        value={selectedCampaign ? String(selectedCampaign.id) : ''}
        onSelect={onChangeTemplate}
        items={campaigns.map((item) => ({ label: item.name, value: String(item.id) }))}
      />
      <div className={styles.height05rem} />
      <div className={styles.height1rem} />
      <div
        className={
          selectedCampaign && selectedCampaign.campaignType === 'replacement-voucher'
            ? ''
            : styles.talononeCreateContainerHide
        }
      >
        <div className={clsx([styles.text075rem, styles.textGrey])}>{t('modal.talonone_create.note')}</div>
        <div className={styles.talononeCreateRows}>
          <Radio
            label={t('modal.talonone_create.discount_amount')}
            onChange={() => updateState({ ...state, discount: 'Amount' })}
            checked={discount === 'Amount'}
          />
          <div className={styles.height05rem} />
          <div className={styles.talononeCreateRow}>
            <div className={styles.talononeCreateRowCurrency}>
              <Dropdown
                label={t('modal.talonone_create.currency')}
                required={true}
                disabled={discount !== 'Amount'}
                value={amountCurrency ? amountCurrency.code : ''}
                onSelect={(value: string) =>
                  updateState({ ...state, amountCurrency: currencies.find((sec) => sec.code === value) || null })
                }
                isError={discount === 'Amount' && (amountCurrency == null || amountCurrency.code == '')}
                items={currencies.map((item) => ({ label: item.code, value: item.code }))}
              />
            </div>
            <div className={styles.talononeCreateRowGrow}>
              <Input
                label={t('modal.talonone_create.amount')}
                required={true}
                disabled={discount !== 'Amount'}
                value={amountAmount ? String(amountAmount) : ''}
                isError={discount === 'Amount' && !checkPositive(amountAmount)}
                onChange={(value: string) => updateState({ ...state, amountAmount: numberOnly(amountAmount, value) })}
              />
            </div>
            <div className={styles.talononeCreateRowGrow}>
              <Input
                label={t('modal.talonone_create.min_order')}
                required={true}
                disabled={discount !== 'Amount'}
                value={amountMinOrder ? String(amountMinOrder) : ''}
                isError={discount === 'Amount' && !checkPositive(amountMinOrder)}
                onChange={(value: string) =>
                  updateState({ ...state, amountMinOrder: numberOnly(amountMinOrder, value) })
                }
              />
            </div>
          </div>
        </div>
        <div className={styles.height05rem} />
        <div className={styles.talononeCreateRows}>
          <Radio
            label={t('modal.talonone_create.discount_percentage')}
            onChange={() => updateState({ ...state, discount: 'Percentage' })}
            checked={discount === 'Percentage'}
          />
          <div className={styles.height05rem} />
          <div className={styles.talononeCreateRow}>
            <div className={styles.talononeCreateRowCurrency}>
              <Dropdown
                label={t('modal.talonone_create.currency')}
                required={true}
                disabled={discount !== 'Percentage'}
                value={percentageCurrency ? percentageCurrency.code : ''}
                onSelect={(value: string) =>
                  updateState({ ...state, percentageCurrency: currencies.find((sec) => sec.code === value) || null })
                }
                isError={discount === 'Percentage' && (percentageCurrency == null || percentageCurrency.code == '')}
                items={currencies.map((item) => ({ label: item.code, value: item.code }))}
              />
            </div>
            <div className={styles.talononeCreateRowGrow}>
              <Input
                label={t('modal.talonone_create.max_percentage')}
                required={true}
                disabled={discount !== 'Percentage'}
                value={percentageAmount ? String(percentageAmount) : ''}
                isError={discount === 'Percentage' && checkPercentage(percentageAmount)}
                onChange={(value: string) => {
                  const v = numberOnly(percentageAmount, value);
                  updateState({
                    ...state,
                    percentageAmount: v && v > 50 ? 50 : v && v < 1 ? 1 : v,
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.height1rem} />
      <div className={styles.height1rem} />
      <TalonOneDatePicker
        required={true}
        label={t('modal.talonone_create.expiry_date')}
        selectedDate={date}
        startingDate={currentTime.current}
        setSelectedDate={setDate}
      />
      <div className={styles.height1rem} />
    </div>
  );

  const checkSubmitAmount = () => {
    if (discount !== 'Amount') {
      return true;
    }
    return checkPositive(amountAmount) && checkPositive(amountMinOrder);
  };

  const checkSubmitPercentage = () => {
    if (discount !== 'Percentage') {
      return true;
    }
    return checkPositive(percentageAmount);
  };

  const checkDiscount = () => {
    if (selectedCampaign == null) {
      return false;
    }
    return selectedCampaign.campaignType === 'replacement-voucher' ? discount !== 'None' : true;
  };

  const checkAmountCurrency = () => {
    if (selectedCampaign == null) {
      return false;
    }
    if (selectedCampaign.campaignType !== 'replacement-voucher') {
      return true;
    }
    if (discount !== 'Amount') {
      return true;
    }
    return amountCurrency != null && amountCurrency.code != '';
  };

  const checkPercentageCurrency = () => {
    if (selectedCampaign == null) {
      return false;
    }
    if (selectedCampaign.campaignType !== 'replacement-voucher') {
      return true;
    }
    if (discount !== 'Percentage') {
      return true;
    }
    return percentageCurrency != null && percentageCurrency.code != '';
  };

  const submittable =
    submitR.kind !== 'Loading' &&
    selectedCampaign != null &&
    date != null &&
    checkDiscount() &&
    checkSubmitAmount() &&
    checkSubmitPercentage() &&
    checkAmountCurrency() &&
    checkPercentageCurrency();

  const params: NewPromoCodeData = {
    campaignId: selectedCampaign ? selectedCampaign.id : 0,
    customerEmail: client.email,
    expiryDateTime: date || '',
    mov: discount === 'None' ? undefined : discount === 'Amount' ? amountMinOrder || undefined : undefined,
    currency:
      discount === 'None'
        ? undefined
        : discount === 'Amount' && amountCurrency
        ? amountCurrency.code
        : percentageCurrency
        ? percentageCurrency.code
        : undefined,
    amount: discount === 'Amount' && amountAmount ? amountAmount : undefined,
    percent: discount === 'Percentage' && percentageAmount ? percentageAmount : undefined,
    couponDiscountType: discount === 'None' ? undefined : discount === 'Amount' ? 'flat' : 'percentage',
    clientId: client.clientId,
  };

  return (
    <StandardModal
      title={t('modal.talonone_create.title')}
      onClose={onClose}
      onSubmit={() => submitR.mutate(params)}
      submittable={submittable}
      submitLabel={t('common.create').toUpperCase()}
      body={body}
      containerStyle={{ maxWidth: 500 }}
      modalName="talonone_create"
    />
  );
};

function filterCampaigns(items: TalonOneCampaign[]): TalonOneCampaign[] {
  return items.filter(({ status }) => ['disabled', 'expired', 'draft'].includes(status) === false);
}

function numberOnly(d: number | null, s: string): number | null {
  const i = Number(s);
  return s === '' ? null : Number.isInteger(i) === false ? d : i;
}

function checkPercentage(amount: number | null): boolean {
  return Number.isInteger(amount) === false || (amount != null && amount < 1) || (amount != null && amount > 50);
}

function checkPositive(amount: number | null): boolean {
  return amount != null && Number.isInteger(amount) === true && amount > 0;
}

export default View;
